import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import Input from "rs-components/Input";
import TextInput from "rs-components/Input/components/TextInput";
import TagsSelectInput from "rs-components/Input/components/TagsSelectInput";
import LearnMoreLink from "rs-components/LearnMoreLink";
import { HELP_SYSTEM_URL, HelpId } from "shared/helpSystemBindings";
import { GlobalEntityType } from "shared/types";
import { psTagsListSelector, rsSystemTagsNamesSelector, rsTagsListSelector, } from "store/global/global.selectors";
import { getErrorByPropertyName, getFieldRestrictions, getRequestName, } from "store/knowledge-base-item/knowledge-base-item.utils";
import { removeErrorByPropertyName } from "store/knowledge-base-item/knowledge-base-item.actions";
import * as styles from "./styles.module.scss";
export var EntityFormFieldsNames;
(function (EntityFormFieldsNames) {
    EntityFormFieldsNames["NAME"] = "name";
    EntityFormFieldsNames["TAGS"] = "tags";
    EntityFormFieldsNames["DESCRIPTION"] = "description";
})(EntityFormFieldsNames || (EntityFormFieldsNames = {}));
const EntityFormFields = ({ entityType, restrictions, serverErrors = null, fieldName = "", errorFieldName = "", learnMoreUrl, submitting = false, onNameFieldChange, }) => {
    const dispatch = useDispatch();
    const tagsList = useSelector(entityType === GlobalEntityType.PsWorkspace ? psTagsListSelector : rsTagsListSelector);
    const systemTagsNames = useSelector(rsSystemTagsNamesSelector);
    const helpUrl = useMemo(() => {
        if (!entityType) {
            return;
        }
        if (learnMoreUrl) {
            return learnMoreUrl;
        }
        switch (entityType) {
            case GlobalEntityType.Recipe:
                return `${HELP_SYSTEM_URL}#cshid=${HelpId.NEW_RECIPE}`;
            case GlobalEntityType.CellModel:
                return `${HELP_SYSTEM_URL}#cshid=${HelpId.NEW_CELL_MODEL}`;
            case GlobalEntityType.RsWorkspace:
                return `${HELP_SYSTEM_URL}#cshid=${HelpId.POPUP_NEW_WS}`;
            default:
                return;
        }
    }, [entityType, learnMoreUrl]);
    const nameError = useMemo(() => getErrorByPropertyName([
        getRequestName(`${errorFieldName || fieldName}${EntityFormFieldsNames.NAME}`),
        `${errorFieldName || fieldName}${EntityFormFieldsNames.NAME}`,
    ], serverErrors), [errorFieldName, fieldName, serverErrors]);
    const tagsError = useMemo(() => getErrorByPropertyName([
        getRequestName(`${errorFieldName || fieldName}${EntityFormFieldsNames.TAGS}`),
        `${errorFieldName || fieldName}${EntityFormFieldsNames.TAGS}`,
    ], serverErrors), [errorFieldName, fieldName, serverErrors]);
    const descriptionError = useMemo(() => getErrorByPropertyName([
        getRequestName(`${errorFieldName || fieldName}${EntityFormFieldsNames.DESCRIPTION}`),
        `${errorFieldName || fieldName}${EntityFormFieldsNames.DESCRIPTION}`,
    ], serverErrors), [errorFieldName, fieldName, serverErrors]);
    const resetNameError = useCallback(() => {
        if (nameError) {
            dispatch(removeErrorByPropertyName([
                getRequestName(`${errorFieldName || fieldName}${EntityFormFieldsNames.NAME}`),
                `${errorFieldName || fieldName}${EntityFormFieldsNames.NAME}`,
            ]));
        }
        if (onNameFieldChange) {
            onNameFieldChange();
        }
    }, [dispatch, errorFieldName, fieldName, nameError, onNameFieldChange]);
    return (React.createElement(React.Fragment, null,
        helpUrl && React.createElement(LearnMoreLink, { className: styles.linkBlock, url: helpUrl }),
        React.createElement("div", { className: styles.fields },
            React.createElement(Input, { component: TextInput, restrictions: getFieldRestrictions(restrictions, [EntityFormFieldsNames.NAME]), name: `${fieldName}${EntityFormFieldsNames.NAME}`, label: "Name", onChange: resetNameError, externalError: nameError?.text, autoComplete: "off", autoFocus: entityType !== GlobalEntityType.PsWorkspace, classes: {
                    container: classNames(styles.field, styles["name-field"]),
                    component: styles.input,
                }, suppressErrors: submitting }),
            React.createElement(Input, { component: TagsSelectInput, label: "Tags", name: `${fieldName}${EntityFormFieldsNames.TAGS}`, classes: { container: styles.tagField }, items: tagsList.filter(({ removable }) => removable), forbiddenTagsNames: entityType === GlobalEntityType.PsWorkspace ? [] : systemTagsNames, externalError: tagsError?.text, restrictions: getFieldRestrictions(restrictions, [EntityFormFieldsNames.TAGS]) }),
            React.createElement(Input, { component: TextInput, restrictions: getFieldRestrictions(restrictions, [EntityFormFieldsNames.DESCRIPTION]), name: `${fieldName}${EntityFormFieldsNames.DESCRIPTION}`, label: "Note", textarea: true, autoHeightMin: 184, autoHeightMax: 184, classes: { container: styles.field, component: styles.input }, externalError: descriptionError?.text }))));
};
export default EntityFormFields;
